// Gallery Walk — React port of kula-create's gallery-walk.html.
//
// Pedagogy: multi-topic exploration. Self-paced movement through stations,
// each with content (text/image/quote) + a reflection prompt. After visiting
// all stations the synthesis prompt lands.
//
// Single-row capture per slide on synthesis-submit:
//   { visited: string[], reflections: { [stationId]: string }, synthesis: string }

import { useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { GalleryWalkSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';
import { RisographGalleryWalk } from './RisographGalleryWalk';

interface Props {
  data: GalleryWalkSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: GalleryWalkSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  /** Called exactly once when the learner submits the walk. */
  onComplete?: () => void;
  /** Last recorded response payload for this slide — restores the submitted view. */
  savedResponse?: Record<string, unknown> | null;
}

export function GalleryWalkSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete, savedResponse }: Props) {
  if (isEditing) return <GalleryWalkEditor data={data} theme={theme} onUpdate={onUpdate} />;
  // Tier-A bespoke renderer for the "Risograph Assembly" world (home-set pairing #2).
  // The bespoke renderer fires onComplete once at its terminal submit and
  // rehydrates from savedResponse, so both are wired through here too.
  if (theme.id === 'risograph') {
    return <RisographGalleryWalk data={data} theme={theme} onActivityResponse={onActivityResponse} onComplete={onComplete} savedResponse={savedResponse} />;
  }
  return (
    <GalleryWalkStudent
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

function GalleryWalkStudent({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: GalleryWalkSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  // The payload ({visited, reflections, synthesis}) only fires on submit, so its
  // presence means the walk finished — restore the submitted view from it.
  const saved: Record<string, unknown> = savedResponse ?? {};
  const restored = Array.isArray(saved.visited);

  const [openStationId, setOpenStationId] = useState<string | null>(null);
  const [openedIds, setOpenedIds] = useState<Set<string>>(
    () => new Set(restored ? (saved.visited as string[]) : []),
  );
  const [reflections, setReflections] = useState<Record<string, string>>(() =>
    restored && saved.reflections && typeof saved.reflections === 'object'
      ? { ...(saved.reflections as Record<string, string>) }
      : {},
  );
  const [synthesis, setSynthesis] = useState(
    restored && typeof saved.synthesis === 'string' ? saved.synthesis : '',
  );
  const [submitted, setSubmitted] = useState(restored);
  const completedRef = useRef(restored);

  // A station counts as visited once OPENED; the set is monotonic. Stations with
  // a reflection prompt additionally need a non-empty reflection before the walk
  // can finish.
  const visited = data.stations.filter((s) => openedIds.has(s.id)).map((s) => s.id);
  const allVisited = visited.length === data.stations.length && data.stations.length > 0;
  const reflectionsDone = data.stations.every(
    (s) => s.reflectionPrompt.trim().length === 0 || (reflections[s.id] ?? '').trim().length > 0,
  );
  const canFinish = allVisited && reflectionsDone;
  const hasSynthesisPrompt = (data.synthesis?.prompt ?? '').trim().length > 0;

  const submit = useCallback(() => {
    setSubmitted(true);
    const trimmedReflections = Object.fromEntries(
      Object.entries(reflections).map(([id, v]) => [id, v.trim()]),
    );
    const responseData: Record<string, unknown> = { visited, reflections: trimmedReflections };
    // Only include synthesis when the author actually set a prompt.
    if (hasSynthesisPrompt) responseData.synthesis = synthesis.trim();
    onActivityResponse?.({
      activityType: 'gallery-walk',
      mode: 'survey',
      responseData,
    });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [visited, reflections, synthesis, hasSynthesisPrompt, onActivityResponse, onComplete]);

  // The learner's note from each station, shown back together. Fixes the review's
  // "stations collapse at synthesis" problem (they can hold all voices together
  // from their own notes) and the flat "Walk complete." ending.
  const stationEchoes = data.stations.map((s) => ({
    label: s.title,
    value: reflections[s.id] ?? '',
  }));
  const galleryConclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'The walk, together',
    body: 'Here is what each station left you with.',
  };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
          {data.intro && (
            <p className="text-sm mt-3 max-w-xl mx-auto" style={{ color: theme.colors.text }}>
              {data.intro}
            </p>
          )}
        </header>

        {data.stations.length === 0 ? (
          <div
            className="rounded-lg p-6 text-center text-sm"
            style={{ background: theme.colors.surface, color: theme.colors.textMuted }}
          >
            This gallery has no stations yet — there is nothing to explore.
          </div>
        ) : (
          <div className="text-xs mb-4 text-center" style={{ color: theme.colors.textMuted }}>
            Visited: {visited.length} of {data.stations.length}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          {data.stations.map((station, idx) => {
            const isOpen = openStationId === station.id;
            const isVisited = openedIds.has(station.id);
            const hasReflectionPrompt = station.reflectionPrompt.trim().length > 0;
            return (
              <div
                key={station.id}
                className="rounded-lg border"
                style={{
                  background: theme.colors.surface,
                  borderColor: isVisited ? accent : `${theme.colors.textMuted}33`,
                  gridColumn: isOpen ? '1 / -1' : 'auto',
                }}
              >
                <button
                  onClick={() => {
                    // Opening a station marks it visited — the set is monotonic.
                    if (!isOpen) setOpenedIds((prev) => new Set(prev).add(station.id));
                    setOpenStationId(isOpen ? null : station.id);
                  }}
                  className="w-full text-left p-4"
                  style={{ background: 'transparent', cursor: 'pointer' }}
                >
                  <span className="text-xs font-bold tracking-wider uppercase" style={{ color: accent }}>
                    Station {idx + 1}
                    {isVisited && ' · ✓'}
                  </span>
                  <h3 className="font-semibold mt-1" style={{ color: theme.colors.text }}>
                    {station.title}
                  </h3>
                  <p className="text-xs mt-1" style={{ color: theme.colors.textMuted }}>
                    {station.teaser}
                  </p>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <Reveal variant="up" spring="gentle" className="px-4 pb-4 space-y-3">
                      {station.content.map((block, bIdx) => (
                        <GalleryContentBlock key={bIdx} block={block} theme={theme} accent={accent} />
                      ))}
                      {hasReflectionPrompt && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text }}>
                            {station.reflectionPrompt}
                          </label>
                          <textarea
                            value={reflections[station.id] ?? ''}
                            onChange={(e) =>
                              setReflections((prev) => ({ ...prev, [station.id]: e.target.value }))
                            }
                            disabled={submitted}
                            rows={3}
                            className="w-full px-3 py-2 rounded border text-sm"
                            style={{
                              background: theme.colors.background,
                              borderColor: `${theme.colors.textMuted}33`,
                              color: theme.colors.text,
                            }}
                          />
                        </div>
                      )}
                    </Reveal>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {canFinish && !submitted && (
          <div
            className="rounded-xl p-5 shadow-sm"
            style={{ background: theme.colors.surface, border: `2px solid ${accent}` }}
          >
            {hasSynthesisPrompt ? (
              <>
                <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                  Synthesis
                </p>
                {/* Hold all voices together: recap the learner's note from each
                    station so they can synthesise without the stations collapsing
                    out of view (review §6). */}
                <div className="mb-3 space-y-1.5">
                  {stationEchoes
                    .filter((e) => e.value.trim())
                    .map((e, i) => (
                      <div key={i} className="text-xs" style={{ color: theme.colors.textMuted }}>
                        <span className="font-semibold" style={{ color: theme.colors.text }}>
                          {e.label}:
                        </span>{' '}
                        {e.value}
                      </div>
                    ))}
                </div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text }}>
                  {data.synthesis!.prompt}
                </label>
                <textarea
                  value={synthesis}
                  onChange={(e) => setSynthesis(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded border text-sm mb-3"
                  style={{
                    background: theme.colors.background,
                    borderColor: `${theme.colors.textMuted}33`,
                    color: theme.colors.text,
                  }}
                />
                {/* Finish is always available once the walk is done — the
                    synthesis note is optional, so we never gate submit on it. */}
                <button
                  onClick={submit}
                  className="w-full px-6 py-3 rounded-lg font-semibold text-sm"
                  style={{ background: accent, color: '#fff' }}
                >
                  Submit reflection
                </button>
              </>
            ) : (
              <button
                onClick={submit}
                className="w-full px-6 py-3 rounded-lg font-semibold text-sm"
                style={{ background: accent, color: '#fff' }}
              >
                Finish walk
              </button>
            )}
          </div>
        )}

        {/* Authored close — replaces the flat "Walk complete." with the station
            voices shown back together + the authored "So what?". */}
        {submitted && (
          <Reveal variant="up" className="mt-2">
            <ExperienceClose
              conclusion={galleryConclusion}
              theme={theme}
              echoes={
                hasSynthesisPrompt
                  ? [...stationEchoes, { label: 'Your synthesis', value: synthesis }]
                  : stationEchoes
              }
            />
          </Reveal>
        )}
      </div>
    </div>
  );
}

function GalleryContentBlock({
  block,
  theme,
  accent,
}: {
  block: GalleryWalkSlideData['stations'][number]['content'][number];
  theme: Theme;
  accent: string;
}) {
  if (block.type === 'image') {
    return (
      <figure>
        <img src={block.value} alt={block.attribution ?? ''} className="w-full rounded" />
        {block.attribution && (
          <figcaption className="text-xs mt-1 italic" style={{ color: theme.colors.textMuted }}>
            {block.attribution}
          </figcaption>
        )}
      </figure>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote className="border-l-4 pl-3 py-1" style={{ borderColor: accent }}>
        <p className="italic text-sm" style={{ color: theme.colors.text }}>“{block.value}”</p>
        {block.attribution && (
          <p className="text-xs mt-1" style={{ color: theme.colors.textMuted }}>— {block.attribution}</p>
        )}
      </blockquote>
    );
  }
  return (
    <p className="text-sm leading-relaxed" style={{ color: theme.colors.text }}>
      {block.value}
    </p>
  );
}

// ---- Editor (minimal) -----------------------------------------------------

function GalleryWalkEditor({
  data,
  theme,
  onUpdate,
}: {
  data: GalleryWalkSlideData;
  theme: Theme;
  onUpdate: (data: GalleryWalkSlideData) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const inputStyle = {
    background: theme.colors.background,
    borderColor: `${theme.colors.textMuted}33`,
    color: theme.colors.text,
  } as const;

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[760px] space-y-4">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <textarea
          value={data.intro}
          onChange={(e) => onUpdate({ ...data, intro: e.target.value })}
          placeholder="Intro (frames the gallery)"
          rows={2}
          className="w-full px-3 py-2 rounded border text-sm"
          style={inputStyle}
        />

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Stations</label>
            <button
              onClick={() =>
                onUpdate({
                  ...data,
                  stations: [
                    ...data.stations,
                    { id: crypto.randomUUID(), title: '', teaser: '', content: [], reflectionPrompt: '' },
                  ],
                })
              }
              className="text-xs"
              style={{ color: accent }}
            >
              + Add station
            </button>
          </div>
          {data.stations.map((s, idx) => (
            <div key={s.id} className="rounded-lg border p-4 mb-3" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold" style={{ color: accent }}>Station {idx + 1}</span>
                <button
                  onClick={() => onUpdate({ ...data, stations: data.stations.filter((x) => x.id !== s.id) })}
                  className="text-xs text-gray-400 hover:text-red-500"
                >
                  Remove
                </button>
              </div>
              <input
                value={s.title}
                onChange={(e) => onUpdate({ ...data, stations: data.stations.map((x) => (x.id === s.id ? { ...x, title: e.target.value } : x)) })}
                placeholder="Station title"
                className="w-full mb-2 px-2 py-1 rounded border text-sm font-semibold"
                style={inputStyle}
              />
              <input
                value={s.teaser}
                onChange={(e) => onUpdate({ ...data, stations: data.stations.map((x) => (x.id === s.id ? { ...x, teaser: e.target.value } : x)) })}
                placeholder="One-line teaser"
                className="w-full mb-2 px-2 py-1 rounded border text-sm"
                style={inputStyle}
              />
              {s.content.map((block, bIdx) => (
                <div key={bIdx} className="rounded p-2 mb-2 flex gap-2" style={{ background: theme.colors.background }}>
                  <select
                    value={block.type}
                    onChange={(e) => {
                      const next = [...s.content];
                      next[bIdx] = { ...block, type: e.target.value as 'text' | 'image' | 'quote' };
                      onUpdate({ ...data, stations: data.stations.map((x) => (x.id === s.id ? { ...x, content: next } : x)) });
                    }}
                    className="text-xs px-2 py-1 rounded border"
                    style={inputStyle}
                  >
                    <option value="text">Text</option>
                    <option value="image">Image URL</option>
                    <option value="quote">Quote</option>
                  </select>
                  <textarea
                    value={block.value}
                    onChange={(e) => {
                      const next = [...s.content];
                      next[bIdx] = { ...block, value: e.target.value };
                      onUpdate({ ...data, stations: data.stations.map((x) => (x.id === s.id ? { ...x, content: next } : x)) });
                    }}
                    placeholder="Content"
                    rows={2}
                    className="flex-1 px-2 py-1 rounded border text-xs"
                    style={inputStyle}
                  />
                  <button
                    onClick={() => {
                      const next = s.content.filter((_, i) => i !== bIdx);
                      onUpdate({ ...data, stations: data.stations.map((x) => (x.id === s.id ? { ...x, content: next } : x)) });
                    }}
                    className="text-xs text-gray-400 hover:text-red-500"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const next = [...s.content, { type: 'text' as const, value: '' }];
                  onUpdate({ ...data, stations: data.stations.map((x) => (x.id === s.id ? { ...x, content: next } : x)) });
                }}
                className="text-xs"
                style={{ color: accent }}
              >
                + Add content block
              </button>
              <textarea
                value={s.reflectionPrompt}
                onChange={(e) => onUpdate({ ...data, stations: data.stations.map((x) => (x.id === s.id ? { ...x, reflectionPrompt: e.target.value } : x)) })}
                placeholder="Reflection prompt"
                rows={2}
                className="w-full mt-2 px-2 py-1 rounded border text-sm"
                style={inputStyle}
              />
            </div>
          ))}
        </div>

        <div className="rounded-lg border p-4" style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}>
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>Synthesis prompt</label>
          <textarea
            value={data.synthesis?.prompt ?? ''}
            onChange={(e) => onUpdate({ ...data, synthesis: { prompt: e.target.value } })}
            rows={2}
            className="w-full mt-2 px-3 py-2 rounded border text-sm"
            style={inputStyle}
          />
        </div>
      </div>
    </div>
  );
}
